import type React from 'react';
import { UNSTABLE_ToastQueue as ToastQueue } from 'react-aria-components';

/**
 * The one queue every toast in the product goes through, and the imperative
 * API a Redux middleware, a mutation handler or a component calls to reach it.
 * It lives outside React on purpose: three middlewares fire toasts with no
 * component in sight. React Aria's queue is the store; `ToastRegion` renders
 * whatever is in it. The queue's content is an observable item so an update
 * lands in place, and the clocks are the centre's so an update can change
 * them (toast.md).
 */

export type ToastStatus = 'info' | 'success' | 'warning' | 'error' | 'loading';

export interface ToastContent {
  /** What the message means: the icon and its colour, and how it is announced. */
  status: ToastStatus;
  /** The one line that says what happened. */
  title: React.ReactNode;
  /** More, when the title is not enough. */
  description?: React.ReactNode;
  /** One thing the user can do about it: undo, view, retry. Pressing it closes the toast. */
  action?: { label: string; onPress: () => void };
}

export interface ToastShowOptions {
  /** A stable id: showing a toast whose id is already on screen updates it instead of stacking a twin. */
  id?: string;
  /** Milliseconds, or `null` to stay until dismissed; 5000 by default, `null` while loading or with an action. */
  duration?: number | null;
  /** Called once when the toast has gone, dismissed or expired. */
  onClose?: () => void;
}

export type ToastUpdate = Partial<ToastContent> & Pick<ToastShowOptions, 'duration'>;

export interface ToastHandle {
  id: string;
  /** Change what the toast says, its status, or how long it stays, in place. */
  update: (patch: ToastUpdate) => void;
  close: () => void;
}

export const DEFAULT_TOAST_DURATION = 5000;

/** The queue's content: what the card renders, observable so an update reaches it in place. */
export class ToastItem {
  key = '';
  private listeners = new Set<() => void>();

  constructor(
    readonly id: string,
    private content: ToastContent,
  ) {}

  get = (): ToastContent => this.content;

  set(patch: Partial<ToastContent>) {
    this.content = { ...this.content, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

/* A pausable countdown, the same shape React Aria keeps privately. */
class Countdown {
  private handle: ReturnType<typeof setTimeout> | null = null;
  private startedAt = 0;
  private remaining: number;

  constructor(
    private readonly onExpire: () => void,
    delay: number,
  ) {
    this.remaining = delay;
    this.resume();
  }

  pause() {
    if (this.handle === null) return;
    clearTimeout(this.handle);
    this.handle = null;
    this.remaining -= Date.now() - this.startedAt;
  }

  resume() {
    if (this.handle !== null) return;
    this.startedAt = Date.now();
    this.handle = setTimeout(this.onExpire, Math.max(0, this.remaining));
  }

  clear() {
    if (this.handle !== null) clearTimeout(this.handle);
    this.handle = null;
  }
}

let sequence = 0;

/* React Aria's region pauses and resumes the queue while the pointer or focus
 * is on it; the clocks are the centre's, so the queue hands the hold over. */
class HeldQueue extends ToastQueue<ToastItem> {
  constructor(private readonly center: ToastCenter) {
    super({ maxVisibleToasts: 5 });
  }

  override pauseAll() {
    this.center.pauseAll();
  }

  override resumeAll() {
    this.center.resumeAll();
  }
}

export class ToastCenter {
  readonly queue: ToastQueue<ToastItem> = new HeldQueue(this);
  private readonly active = new Map<string, ToastItem>();
  private readonly clocks = new Map<string, Countdown>();
  private paused = false;

  constructor() {
    this.queue.subscribe(() => this.syncClocks());
  }

  show(content: ToastContent, options: ToastShowOptions = {}): ToastHandle {
    const id = options.id ?? `ck-toast-${(sequence += 1)}`;
    if (this.active.has(id)) {
      this.update(id, {
        ...content,
        duration: options.duration === undefined ? defaultDuration(content) : options.duration,
      });
      return this.handle(id);
    }
    const item = new ToastItem(id, content);
    item.key = this.queue.add(item, {
      onClose: () => {
        this.clocks.get(id)?.clear();
        this.clocks.delete(id);
        this.active.delete(id);
        /* The last toast can leave under the pointer, so no pointerleave
         * ever releases the hold; an empty stack is never paused. */
        if (this.active.size === 0) this.paused = false;
        options.onClose?.();
      },
    });
    this.active.set(id, item);
    this.retime(id, options.duration === undefined ? defaultDuration(content) : options.duration);
    return this.handle(id);
  }

  update(id: string, patch: ToastUpdate) {
    const item = this.active.get(id);
    if (!item) return;
    const { duration, ...content } = patch;
    item.set(content);
    if (duration !== undefined) this.retime(id, duration);
    else if (content.status !== undefined || 'action' in content) {
      this.retime(id, defaultDuration(item.get()));
    }
  }

  close(id: string) {
    const item = this.active.get(id);
    if (item) this.queue.close(item.key);
  }

  isActive(id: string) {
    return this.active.has(id);
  }

  /** Every toast on screen, gone: a test's teardown, a sign-out. */
  closeAll() {
    [...this.active.values()].forEach((item) => this.queue.close(item.key));
  }

  pauseAll() {
    this.paused = true;
    this.syncClocks();
  }

  resumeAll() {
    this.paused = false;
    this.syncClocks();
  }

  /* A toast waiting behind the five on screen has not been seen yet, so its
   * clock waits with it. */
  private syncClocks() {
    const visible = new Set(this.queue.visibleToasts.map((toast) => toast.content.id));
    this.clocks.forEach((clock, id) => {
      if (this.paused || !visible.has(id)) clock.pause();
      else clock.resume();
    });
  }

  private retime(id: string, duration: number | null) {
    this.clocks.get(id)?.clear();
    this.clocks.delete(id);
    if (duration === null) return;
    this.clocks.set(id, new Countdown(() => this.close(id), duration));
    this.syncClocks();
  }

  private handle(id: string): ToastHandle {
    return {
      id,
      update: (patch) => this.update(id, patch),
      close: () => this.close(id),
    };
  }
}

/* No clock while loading (it ends when the work does) or with an action (a
 * control that can vanish before it is pressed fails WCAG 2.2.1). */
const defaultDuration = (content: ToastContent) =>
  content.status === 'loading' || content.action ? null : DEFAULT_TOAST_DURATION;

/** The product's one toast centre. */
export const toastCenter = new ToastCenter();

/**
 * The imperative API: `toast.show(content, options)` from anywhere, React or
 * not, and the handle it returns to update or close that toast in place.
 */
export const toast = {
  show: (content: ToastContent, options?: ToastShowOptions) => toastCenter.show(content, options),
  update: (id: string, patch: ToastUpdate) => toastCenter.update(id, patch),
  close: (id: string) => toastCenter.close(id),
  isActive: (id: string) => toastCenter.isActive(id),
  closeAll: () => toastCenter.closeAll(),
};
